import { Decoration, DecorationSet } from "prosemirror-view";
import { Plugin, PluginKey } from "prosemirror-state";
import AbstractAction from "../AbstractAction.js";
import SuggestionMatcher from "./SuggestionMatcher.js";
import SuggestionRenderer from "./SuggestionRenderer.js";

/**
 * Editor action to provide autocompletion.
 * Typing after the selected hashtag character will filter possible values
 * based in the suggestions array provided. Once an item is selected,
 * it will add a node into the editor.
 *
 * @param {string} type - Type of input value to handle. This will be matched
 *   against the `data-type` attribute of the HTML node
 * @param {string} [triggerCharacter] - Character for triggering the suggestions list
 * @param {function(object):boolean} [isSuggestionMatching] - Callback to define the conditions that will apply
 *   to create the suggestions list. This function is invoked with a suggestion and a text, and should return
 *   true if the suggestion matches the text
 * @param {function(object):string} [suggestionToLabel] - Callback that returns the value to label the items
 * @param {function(object):ProseMirror.Node} suggestionToNode - Callback that returns the node to insert into the text
 * @param {string} [activeClass] - css class to add when a .suggestion-item element is active / selected
 * @param {string} [suggestionTextClass] - css class to add to "#Mentioned Text" in editor
 * @param {number} [delay] - Time between the last keystroke and the suggestion menu opening, in ms
 * @param {object[]|Promise} suggestions - List of suggestions, or a Promise that resolve to a list of suggestions.
 *   A suggestion can be anything
 */
export default class SuggestionAction extends AbstractAction {
	constructor({
		type,
		triggerCharacter,
		isSuggestionMatching,
		suggestionToLabel,
		suggestionToNode,
		activeClass,
		suggestionTextClass = "prosemirror-suggestion",
		delay,
		suggestions,
	}) {
		super(...arguments);

		this._type = type;

		this._suggestionToLabel = suggestionToLabel;
		this._suggestionToNode = suggestionToNode;
		this._suggestionTextClass = suggestionTextClass;

		this._renderer = new SuggestionRenderer({
			activeClass,
			suggestionToLabel,
			suggestionTextClass,
			delay,
			getFilteredSuggestions: this._getFilteredSuggestions.bind(this),
			selectCurrentHighlightedSuggestion:
				this._selectCurrentHighlightedSuggestion.bind(this),
		});

		this._matcher = new SuggestionMatcher({
			isSuggestionMatching,
			triggerCharacter,
			suggestionToLabel,
		});

		Promise.resolve(suggestions).then((resolvedSuggestions) => {
			this._suggestions = resolvedSuggestions;
		});
	}

	getPlugins() {
		return [this._makePlugin()];
	}

	_getFilteredSuggestions(text) {
		if (!this._suggestions) {
			return null;
		}

		return this._filterSuggestions(text);
	}

	_makePlugin() {
		const key = new PluginKey(`autosuggestions_${this._type}`);

		return new Plugin({
			key,
			state: {
				init: () => this._getNewState(),
				apply: this._applyTransaction.bind(this),
			},
			props: this._getProp({ key }),
			view: () => this._renderer.getView({ key }),
		});
	}

	_applyTransaction(transaction) {
		const newState = this._getNewState();
		const selection = transaction.selection;
		if (selection.from !== selection.to) {
			return newState;
		}

		const $position = selection.$from;
		const match = this._matcher.getTriggerMatch($position);

		if (match) {
			newState.active = true;
			newState.range = match.range;
			newState.text = match.queryText;
		}

		return newState;
	}

	_getProp({ key }) {
		return {
			handleKeyDown: this._handleKeyDown({ key }),
			decorations: this._makeDecorations({ key }),
		};
	}

	_getNewState() {
		return {
			active: false,
			range: {
				from: 0,
				to: 0,
			},
			text: "",
			suggestions: [],
			index: 0,
		};
	}

	_handleKeyDown({ key }) {
		return (view, event) => {
			const state = key.getState(view.state);

			const space = event.keyCode === 32;

			if (!state.active) {
				return false;
			}

			if (space && this._suggestions?.length) {
				return this._earlySelect({ view, state });
			}

			if (!state.suggestions.length) {
				return false;
			}

			return this._renderer.handleKeyDown({ view, event, state });
		};
	}

	_makeDecorations({ key }) {
		return (editorState) => {
			const { active, range } = key.getState(editorState);

			if (!active) {
				return null;
			}

			return DecorationSet.create(editorState.doc, [
				Decoration.inline(range.from, range.to, {
					nodeName: "span",
					class: this._suggestionTextClass,
				}),
			]);
		};
	}

	_earlySelect({ view, state }) {
		const suggestions = this._filterSuggestions(state.text);
		const item = suggestions[0];

		if (!item) {
			return false;
		}

		this._selectItem({ item, view, state });
		return true;
	}

	_selectCurrentHighlightedSuggestion({ view, state }) {
		const item = state.suggestions[state.index];
		this._selectItem({ item, view, state });
	}

	_selectItem({ item, view, state }) {
		const newNode = this._suggestionToNode({
			type: this._type,
			item,
			schema: view.state.config.schema,
		});

		const tr = view.state.tr.replaceWith(
			state.range.from,
			state.range.to,
			newNode,
		);

		view.dispatch(tr);
	}

	_filterSuggestions(text) {
		return this._suggestions.filter((suggestion) =>
			this._matcher.isSuggestionMatching({ text, suggestion }),
		);
	}
}
